/**
 * SchwarzesBrettView — Content-View für „DAS SCHWARZE BRETT" (NT-SCHWARZES-BRETT-01).
 * Rendert innerhalb der eigenständigen Seite `pages/brett.ts` (eigener Nav-Eintrag, kein
 * Modal). Quartett: `prepareSchwarzesBrettView()` synchron vor dem Öffnen,
 * `renderSchwarzesBrettViewShell()` als Markup, `mountSchwarzesBrettView()` nach dem
 * DOM-Insert (lädt Daten + Listener), `resetSchwarzesBrettViewState()` beim Verlassen.
 * Page-agnostisch — kennt ihren Host-Container nicht, kann in jede Seite eingehängt werden.
 *
 * Öffentlich anonym (Z2) — die API liefert nie playerId/submitter im Board-View.
 *
 * Chris-Feedback (2026-08-30): Kategorien komplett aus der UI entfernt (Backend akzeptiert das
 * Feld weiterhin optional mit Default SONSTIGES, kein Datenverlust). Begriff „Hinweis" war
 * irreführend — überall durch „Vorschlag" ersetzt (nur Anzeige-Text, interne Bezeichner wie
 * Suggestion/SuggestionStatus bleiben unverändert).
 */

import { api, ApiError } from '../services/api.js';
import { showToast } from '../utils/toast.js';

const ROOT_ID = 'brett-board-root';

// Status-Labels dupliziert aus suggestion.constants.ts (Backend) — etabliertes „kein
// Shared-Import für reine Anzeige-Werte"-Pattern (siehe BUG_REPORT_DISCORD_TAGS).
// IN_PRUEFUNG ist seit 2026-08-30 öffentlich+votebar (siehe SCHWARZES_BRETT_PUBLIC_STATUSES).
const STATUS_LABELS: Record<string, string> = {
  IN_PRUEFUNG: 'In Prüfung',
  GEPLANT: 'Geplant',
  IN_ARBEIT: 'In Arbeit',
  UMGESETZT: 'Umgesetzt',
};
const STATUS_ORDER = ['IN_PRUEFUNG', 'GEPLANT', 'IN_ARBEIT', 'UMGESETZT'];

const TITLE_MIN = 8;
const TITLE_MAX = 120;
const BODY_MIN = 20;
const BODY_MAX = 2000;
const HONEYPOT_FIELD = 'website';

interface PublicSuggestion {
  id: string;
  publicNumber: number;
  title: string;
  body: string;
  status: string;
  voteCount: number;
  hasVoted: boolean;
  createdAt: string;
}

let boardCache: PublicSuggestion[] = [];
let voteInFlight = new Set<string>();

export function prepareSchwarzesBrettView(): void {
  boardCache = [];
  voteInFlight = new Set();
}

export function resetSchwarzesBrettViewState(): void {
  boardCache = [];
  voteInFlight = new Set();
}

export function renderSchwarzesBrettViewShell(): string {
  return `
    <div class="brett-intro">
      <p class="settings-meta">
        Schlage ein neues Feature vor, melde einen Verbesserungswunsch, oder zeichne einen
        bestehenden Vorschlag mit — je mehr Mitzeichner, desto sichtbarer die Nachfrage.
        Eingereichte Vorschläge erscheinen hier, sobald sie gesichtet wurden.
      </p>
      <button class="settings-btn" id="brett-submit-open-btn" type="button">Neuen Vorschlag einreichen</button>
    </div>
    <div id="${ROOT_ID}">
      <p class="settings-meta">Lade Vorschläge...</p>
    </div>
  `;
}

export async function mountSchwarzesBrettView(): Promise<void> {
  document.getElementById('brett-submit-open-btn')?.addEventListener('click', openSubmitModal);
  await loadBoard();
}

async function loadBoard(): Promise<void> {
  const root = document.getElementById(ROOT_ID);
  if (!root) return;

  try {
    boardCache = await api.get<PublicSuggestion[]>('/suggestions');
    renderBoard();
  } catch (err) {
    root.innerHTML = `<p class="settings-meta">${escapeHtml(err instanceof ApiError ? err.message : 'Vorschläge konnten nicht geladen werden.')}</p>`;
  }
}

function renderBoard(): void {
  const root = document.getElementById(ROOT_ID);
  if (!root) return;

  if (boardCache.length === 0) {
    root.innerHTML = `<p class="settings-meta">Noch keine öffentlich sichtbaren Vorschläge — sei der Erste.</p>`;
    return;
  }

  let html = '';
  let currentStatus: string | null = null;
  for (const status of STATUS_ORDER) {
    const items = boardCache.filter((s) => s.status === status);
    if (items.length === 0) continue;
    if (currentStatus !== status) {
      html += `<div class="brett-status-header">${escapeHtml(STATUS_LABELS[status] ?? status)}</div>`;
      currentStatus = status;
    }
    html += items.map(renderCard).join('');
  }
  root.innerHTML = html;

  root.querySelectorAll<HTMLElement>('[data-brett-card]').forEach((card) => {
    card.addEventListener('click', (e) => {
      // Klick auf den Mitzeichnen-Button selbst öffnet nicht die Detailansicht.
      if ((e.target as HTMLElement).closest('[data-brett-vote]')) return;
      const id = card.dataset.brettCard;
      if (id) openDetailModal(id);
    });
  });

  root.querySelectorAll<HTMLButtonElement>('[data-brett-vote]').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const id = btn.dataset.brettVote;
      if (id) void handleVote(id);
    });
  });
}

function renderCard(s: PublicSuggestion): string {
  return `
    <div class="brett-card" data-brett-card="${s.id}">
      <div class="brett-card-head">
        <span class="brett-card-number">#${String(s.publicNumber).padStart(4, '0')}</span>
        <div class="brett-card-title">${escapeHtml(s.title)}</div>
      </div>
      <button
        class="brett-vote-btn${s.hasVoted ? ' brett-vote-btn--active' : ''}"
        type="button"
        data-brett-vote="${s.id}"
        ${voteInFlight.has(s.id) ? 'disabled' : ''}
      >▲ ${String(s.voteCount)}</button>
    </div>
  `;
}

async function handleVote(id: string): Promise<void> {
  if (voteInFlight.has(id)) return;
  voteInFlight.add(id);
  const btn = document.querySelector<HTMLButtonElement>(`[data-brett-vote="${id}"]`);
  if (btn) btn.disabled = true;

  try {
    const result = await api.post<{ voted: boolean; voteCount: number }>(`/suggestions/${id}/vote`, {});
    boardCache = boardCache.map((s) =>
      s.id === id ? { ...s, hasVoted: result.voted, voteCount: result.voteCount } : s,
    );
    if (btn) {
      btn.textContent = `▲ ${String(result.voteCount)}`;
      btn.classList.toggle('brett-vote-btn--active', result.voted);
    }
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Mitzeichnen fehlgeschlagen.', 'error');
  } finally {
    voteInFlight.delete(id);
    if (btn) btn.disabled = false;
  }
}

// ─── Detail-Modal ──────────────────────────────────────────────────────────────

function openDetailModal(id: string): void {
  const suggestion = boardCache.find((s) => s.id === id);
  if (!suggestion) return;

  const overlay = document.createElement('div');
  overlay.className = 'modal-backdrop';
  overlay.innerHTML = `
    <div class="modal" role="dialog" aria-modal="true" aria-label="Vorschlag-Details">
      <div class="modal-body">
        <p class="brett-card-number">#${String(suggestion.publicNumber).padStart(4, '0')}</p>
        <h3 class="brett-detail-title">${escapeHtml(suggestion.title)}</h3>
        <p class="brett-detail-body">${escapeHtml(suggestion.body)}</p>
      </div>
      <div class="modal-actions">
        <button class="settings-btn" id="brett-detail-close-btn" type="button">Schließen</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);

  const close = (): void => { overlay.remove(); };
  overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });
  overlay.querySelector('#brett-detail-close-btn')?.addEventListener('click', close);
}

// ─── Einreich-Formular ─────────────────────────────────────────────────────────

function openSubmitModal(): void {
  const overlay = document.createElement('div');
  overlay.className = 'modal-backdrop';
  overlay.innerHTML = `
    <div class="modal" role="dialog" aria-modal="true" aria-label="Vorschlag einreichen">
      <div class="modal-body">
        <h3 class="brett-detail-title">Neuen Vorschlag einreichen</h3>
        <form id="brett-submit-form" novalidate>
          <div class="bugreport-field bugreport-honeypot" aria-hidden="true">
            <label for="brett-website">Website</label>
            <input type="text" id="brett-website" name="${HONEYPOT_FIELD}" tabindex="-1" autocomplete="off">
          </div>
          <div class="bugreport-field">
            <label class="bugreport-label" for="brett-title">Titel (mind. ${String(TITLE_MIN)} Zeichen)</label>
            <input class="bugreport-input" type="text" id="brett-title" minlength="${String(TITLE_MIN)}" maxlength="${String(TITLE_MAX)}" required>
          </div>
          <div class="bugreport-field">
            <label class="bugreport-label" for="brett-body">Beschreibung (mind. ${String(BODY_MIN)} Zeichen)</label>
            <textarea class="bugreport-textarea" id="brett-body" minlength="${String(BODY_MIN)}" maxlength="${String(BODY_MAX)}" required></textarea>
          </div>
          <div class="settings-hint" id="brett-submit-hint"></div>
        </form>
      </div>
      <div class="modal-actions">
        <button class="settings-btn" id="brett-submit-cancel-btn" type="button">Abbrechen</button>
        <button class="settings-btn" id="brett-submit-confirm-btn" type="submit" form="brett-submit-form">Einreichen</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);

  const close = (): void => { overlay.remove(); };
  overlay.querySelector('#brett-submit-cancel-btn')?.addEventListener('click', close);

  const form = overlay.querySelector<HTMLFormElement>('#brett-submit-form');
  form?.addEventListener('submit', (e) => {
    e.preventDefault();
    void handleSubmit(overlay, close);
  });
}

async function handleSubmit(overlay: HTMLElement, close: () => void): Promise<void> {
  const hint = overlay.querySelector('#brett-submit-hint');
  const submitBtn = overlay.querySelector<HTMLButtonElement>('#brett-submit-confirm-btn');
  if (hint) hint.textContent = '';
  if (submitBtn) submitBtn.disabled = true;

  const title = requireFieldValue(overlay, '#brett-title');
  const body = requireFieldValue(overlay, '#brett-body');
  const website = requireFieldValue(overlay, '#brett-website');

  try {
    await api.post('/suggestions', { title, body, [HONEYPOT_FIELD]: website });
    close();
    showToast('Danke, dein Vorschlag wurde übermittelt und wird geprüft.', 'info');
  } catch (err) {
    if (hint) hint.textContent = extractErrorMessage(err, 'Einreichen fehlgeschlagen. Bitte später erneut versuchen.');
  } finally {
    if (submitBtn) submitBtn.disabled = false;
  }
}

interface ZodIssueLike {
  message: string;
}

function isZodIssueLike(value: unknown): value is ZodIssueLike {
  return (
    typeof value === 'object' &&
    value !== null &&
    'message' in value &&
    typeof value.message === 'string'
  );
}

/**
 * Zeigt bei einer Zod-VALIDATION_ERROR die konkrete Fehlermeldung des ersten betroffenen
 * Felds (z.B. "Titel muss mindestens 8 Zeichen haben.") statt der generischen
 * "Ungültige Eingabe." — sonst weiß der Spieler nicht, was am Formular falsch war.
 */
function extractErrorMessage(err: unknown, fallback: string): string {
  if (!(err instanceof ApiError)) return fallback;

  const issues = err.details.issues;
  if (Array.isArray(issues) && issues.length > 0 && isZodIssueLike(issues[0])) {
    return issues[0].message;
  }
  return err.message;
}

// instanceof-Narrowing statt `as`/`!`-Assertion — vermeidet non-nullable-type-assertion-style
// UND no-non-null-assertion gleichzeitig (beide Regeln sind hier aktiv).
function requireFieldValue(root: HTMLElement, selector: string): string {
  const el = root.querySelector(selector);
  if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement) {
    return el.value;
  }
  return '';
}

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
